import React, { useId } from "react";

import { useTranslation } from "react-i18next";

import { Button } from "@/src/components/ui/button";
import { Progress } from "@/src/components/ui/progress";
import { APP_TOOLTIP_ID } from "@/src/constants/tooltip";

import type { JSX, ReactNode } from "react";

import type { BulkSummary } from "./bulkObjects";
import type { BulkIngestStatus } from "../../api/BulkAPI.types";
import type { StartIngestBlockedReason } from "../../hooks/useBulkIngest";

interface BulkStatusBannerProps {
  actions?: ReactNode;
  children?: ReactNode;
  onStartIngest: () => void;
  startIngestBlockedReason: StartIngestBlockedReason | null;
  status: BulkIngestStatus;
  summary: BulkSummary;
}

function getProgressValue(summary: BulkSummary): number {
  if (summary.total === 0) {
    return 0;
  }

  return Math.round(
    ((summary.total - summary.counts.detected) / summary.total) * 100,
  );
}

function BulkStatusBanner({
  actions,
  children,
  onStartIngest,
  startIngestBlockedReason,
  status,
  summary,
}: BulkStatusBannerProps): JSX.Element {
  const { t } = useTranslation("editor-bulk");
  const blockedReasonId = useId();
  const blockedReasonLabel =
    startIngestBlockedReason === null
      ? undefined
      : t(`startIngestBlocked.${startIngestBlockedReason}`);
  const isStartBlocked = blockedReasonLabel !== undefined;
  const isCreated = status === "created";
  const hasActions = actions !== undefined && actions !== null;
  const hasChildren = children !== undefined && children !== null;

  return (
    <div className="border-border flex flex-wrap items-center gap-3 rounded-xl border p-4">
      <span className="text-sm">
        {t(`ingestMessage.${status}`, { defaultValue: "" })}
      </span>

      {isCreated || hasActions ? (
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {isCreated ? (
            <span
              className="inline-flex"
              data-tooltip-content={blockedReasonLabel}
              data-tooltip-id={APP_TOOLTIP_ID}
            >
              <Button
                aria-describedby={isStartBlocked ? blockedReasonId : undefined}
                disabled={isStartBlocked}
                onClick={onStartIngest}
                type="button"
              >
                {t("start_ingest")}
              </Button>

              {isStartBlocked ? (
                <span className="sr-only" id={blockedReasonId}>
                  {blockedReasonLabel}
                </span>
              ) : null}
            </span>
          ) : null}

          {actions}
        </div>
      ) : null}

      {hasChildren ? <div className="w-full">{children}</div> : null}

      {status === "doing" ? (
        <Progress className="w-full" value={getProgressValue(summary)} />
      ) : null}
    </div>
  );
}

export default BulkStatusBanner;
